import { Check, GraduationCap } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal } from "../components/Reveal";
import { CTASection, Marquee } from "../components/Sections";
import { SITE, IMG } from "../lib/config";

const values = [
  "Escucha activa y sin juicios",
  "Cercanía y trato humano",
  "Respeto por tu ritmo",
  "Confidencialidad y secreto profesional",
];

export default function SobreMi() {
  useSEO({
    title: "Victoria Fuentes | Psicóloga sanitaria en Málaga",
    description:
      "Victoria Fuentes, psicóloga sanitaria en Málaga. Atención psicológica para adultos, adolescentes y niños, presencial y online, desde un enfoque cercano y personalizado.",
    path: "/sobre-mi",
    image: IMG.portrait,
  });

  return (
    <div data-testid="sobre-mi-page">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-36 md:px-12 md:pt-44">
        <Reveal>
          <p className="overline">Sobre mí</p>
          <h1 className="mt-5 max-w-3xl font-serif text-5xl font-light leading-[1.05] tracking-tight text-ink md:text-7xl">
            Victoria Fuentes, psicóloga sanitaria en Málaga
          </h1>
          <p className="mt-5 max-w-2xl font-serif text-2xl font-light italic text-ink-soft md:text-3xl">
            Detrás de la consulta, una persona que te escucha
          </p>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-6 pb-24 md:grid-cols-12 md:px-12 md:pb-32">
        <div className="space-y-6 md:col-span-5">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem]">
              <img src={IMG.portrait} alt="Retrato de Victoria Fuentes, psicóloga sanitaria en Málaga" loading="lazy" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-4xl border border-ink/10 bg-bg-primary p-7" data-testid="formacion">
              <p className="overline flex items-center gap-2"><GraduationCap className="h-4 w-4 text-sage-dark" strokeWidth={1.75} /> Formación</p>
              <ul className="mt-5 space-y-4">
                {[
                  { t: "Licenciada en Psicología", u: "Universidad de Málaga" },
                  { t: "Máster en Psicología Sanitaria", u: "Universidad de Huelva" },
                  { t: "Experta en Terapias de Tercera Generación", u: "Universidad de Almería" },
                  { t: "Especialista en Salud Mental Perinatal", u: "UNED" },
                ].map((f) => (
                  <li key={f.t} className="flex items-start gap-3 border-b border-ink/5 pb-4 last:border-0 last:pb-0">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sage" />
                    <div>
                      <p className="text-base font-medium text-ink">{f.t}</p>
                      <p className="text-sm text-ink-muted">{f.u}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7 md:pl-8">
          <Reveal>
            <p className="text-xl leading-relaxed text-ink md:text-2xl">
              Pedir ayuda no siempre es fácil. Este puede ser el primer paso para empezar a comprender lo que te está
              ocurriendo.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-ink-soft">
              Soy <strong className="font-medium text-ink">Victoria Fuentes</strong>, psicóloga sanitaria en Málaga.
              Mi forma de trabajar parte de la escucha, la comprensión y el respeto por la situación particular de
              cada persona.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft">
              La terapia no consiste en aplicar una misma solución a todo el mundo. Por eso, me formo de manera
              continua y adapto las herramientas terapéuticas a las necesidades y objetivos de cada caso, buscando
              ofrecer una atención rigurosa, cercana y personalizada.
            </p>

            <blockquote className="my-8 border-l-2 border-sage pl-6 font-serif text-2xl font-light italic leading-snug text-ink md:text-3xl">
              No hay una única forma de hacer terapia; buscamos juntos el camino que mejor se adapte a ti y a tu
              momento.
            </blockquote>

            <p className="text-lg leading-relaxed text-ink-soft">
              Trabajo desde un <strong className="font-medium text-ink">enfoque integrador</strong>, utilizando
              diferentes herramientas terapéuticas según lo que cada persona necesita.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft">
              Mi objetivo es ofrecerte un espacio seguro, confidencial y sin juicios, en el que puedas comprender
              mejor lo que te ocurre, adquirir recursos y avanzar hacia una mayor autonomía.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft">
              Como psicóloga sanitaria en Málaga, cuento con experiencia en la atención psicológica a adultos,
              adolescentes y niños, acompañando diferentes dificultades emocionales y situaciones vitales, entre
              ellas la ansiedad, el estado de ánimo, el duelo y los procesos relacionados con la etapa perinatal.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <ul className="mt-10 grid gap-3 sm:grid-cols-2">
              {values.map((v) => (
                <li key={v} className="flex items-start gap-3 text-base text-ink">
                  <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-bg-sage">
                    <Check className="h-4 w-4 text-sage-dark" strokeWidth={2} />
                  </span>
                  {v}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 rounded-4xl border border-ink/10 bg-bg-secondary p-8" data-testid="professional-data">
              <p className="overline">Datos profesionales</p>
              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm text-ink-muted">Nº de colegiada</dt>
                  <dd className="text-lg text-ink">{SITE.colegiada}</dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-muted">Colegio profesional</dt>
                  <dd className="text-lg text-ink">{SITE.colegio}</dd>
                </div>
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />
      <CTASection testId="sobremi-cta-section" />
    </div>
  );
}
